/** @jsxImportSource @emotion/react */
import type { AggregatedUserNotification } from './usecase/useCurrentMemberNotification';

import moment from 'moment';
import { Button, Empty, Avatar, message } from 'antd';
import { CloseOutlined, UserOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { LOCALE_KEY } from './const';
import { useTranslation } from 'react-i18next';
import axios from '@shared/helpers/axios';
import formater from '@shared/helpers/currency';
import { ack } from '@/apis/lib/user-notification';
import styles from './styles';

interface NotificationContentProps {
    userNotifications: AggregatedUserNotification;
    onNotificationDeleted?: (notificationId: number) => void;
    onAllNotificationsDeleted?: () => void;
    onNotificationAcked?: (notificationId: number) => void;
    onRefetch?: () => void;
}

const NotificationContent = ({
    userNotifications,
    onNotificationDeleted,
    onAllNotificationsDeleted,
    onNotificationAcked,
    onRefetch,
}: NotificationContentProps) => {
    interface NotificationInterface {
        id?: number;
        acked?: boolean;
        createdAt?: string;
        imageUrl?: string;
        details?: string;
        ctaUrl?: string;
        carry?: {
            SERVICE?: {
                name?: string;
            };
            PROFESSION_DETAIL?: {
                businessName?: string;
            };
            CLIENT?: {
                name?: string;
            };
            FAV_SOLUTION?: {
                zPropertyName?: string;
            };
            MARKER?: {
                name?: string;
            };
            CashBalance?: {
                amount?: number;
            };
            Crowdfund?: {
                fundingName?: string;
                fundingType?: string;
            };
            Payout?: {
                amount?: number;
                reasonDenied?: string;
                zBankAccountName?: string;
                BankAccount?: {
                    bankName?: string;
                };
            };
            MEMBERSHIP?: {
                name?: string;
            };
            MEMBER?: {
                name?: string;
                username?: string;
            };
            FORUM_THREAD?: {
                description?: string;
            };
        };
    }

    const history = useHistory();
    const { t, i18n } = useTranslation(LOCALE_KEY);

    const _formatTime = (notificationCreatedAt?: string) => {
        const now = moment();
        const notif = moment(notificationCreatedAt);
        const duration = moment.duration(now.diff(notif)).locale(i18n.language);
        return `${duration.humanize()} ${t('ago')}`;
    };

    const translate = (notif: NotificationInterface) => {
        const detail = notif.details ?? 'unknown';
        const payout = notif?.carry?.Payout;
        const cashBalance = notif?.carry?.CashBalance;

        return t(`notif_text.${detail}`, {
            NAME: notif?.carry?.MARKER?.name ?? '',
            SOLUTION_NAME: notif?.carry?.FAV_SOLUTION?.zPropertyName ?? '',
            SERVICE_NAME: notif?.carry?.SERVICE?.name ?? '',
            BUSINESS_NAME: notif?.carry?.PROFESSION_DETAIL?.businessName ?? '',
            CLIENT_NAME: notif?.carry?.CLIENT?.name ?? '',
            AMOUNT: formater(
                payout?.amount ?? cashBalance?.amount ?? 0,
                'splitter'
            ),
            FUNDING_NAME: notif?.carry?.Crowdfund?.fundingName ?? '',
            BANK_NAME:
                payout?.zBankAccountName ||
                payout?.BankAccount?.bankName ||
                '',
            REASON: payout?.reasonDenied || '',
            MEMBERSHIP_NAME: notif?.carry?.MEMBERSHIP?.name ?? '',
            MEMBER: notif?.carry?.MEMBER?.name || notif?.carry?.MEMBER?.username || '',
            DESCRIPTION: notif?.carry?.FORUM_THREAD?.description ?? '',
        });
    };

    const handleDeleteAllNotif = async () => {
        try {
            const allNotificationIds: number[] = [];

            userNotifications.topics.forEach(topic => {
                userNotifications.aggregatedData[topic].forEach((notif: any) => {
                    if (notif.id) {
                        allNotificationIds.push(notif.id);
                    }
                });
            });

            if (allNotificationIds.length === 0) {
                message.info(t('no_notifications_to_delete') || 'No notifications to delete');
                return;
            }

            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v2/user-notifications/multiple-remove`,
                {
                    ids: allNotificationIds,
                }
            );

            message.success(
                t('all_notifications_deleted_successfully') ||
                    'All notifications deleted successfully'
            );

            if (onAllNotificationsDeleted) {
                onAllNotificationsDeleted();
            }
        } catch (error: any) {
            console.error('Error deleting all notifications:', error);
            message.error(error.response?.data?.message || 'Failed to delete all notifications');
        }
    };

    const goToNotificationsPage = () => {
        history.push('/p/area/notifications');
    };

    const onNotificationItemClicked = async (notif: NotificationInterface) => {
        if (!notif.acked && notif.id) {
            try {
                await ack(notif.id);
                onNotificationAcked?.(notif.id);
                onRefetch?.();
            } catch (error) {
                console.error('Error acknowledging notification:', error);
            }
        }

        if (notif.ctaUrl) {
            if (notif.ctaUrl.startsWith('http://') || notif.ctaUrl.startsWith('https://')) {
                window.location.href = notif.ctaUrl;
                return;
            }

            window.location.href = process.env.REACT_APP_VUE_CLIENT_URL + notif.ctaUrl;
        }
    };

    const handleDeleteNotification = async (notif: any, index: any) => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/v2/user-notifications/remove/${notif.id}`
            );

            message.success(
                t('notification_deleted_successfully') || 'Notification deleted successfully'
            );

            if (onNotificationDeleted && notif.id) {
                onNotificationDeleted(notif.id);
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to delete notification');
        }
    };

    return (
        <div>
            <div css={styles.notifWrapper}>
                {userNotifications.topics.length === 0 ? (
                    <Empty
                        className="empty-content"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('no_notif')}
                    />
                ) : (
                    <>
                        {userNotifications.topics.map(topic => (
                            <div css={styles.notifContainer} key={topic}>
                                <div className="notification-title">
                                    {t(`notif_text.${topic}.title`)}: (
                                    {userNotifications.aggregatedData[topic].length})
                                </div>
                                {userNotifications.aggregatedData[topic].map((notif, index) => (
                                    <div
                                        key={`${topic}-${index}`}
                                        className={`notification-item${notif.acked ? ' acked-notif' : ''}`}
                                        css={[
                                            {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '12px',
                                                borderBottom: '1px solid #f0f0f0',
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: '#f9f9f9' },
                                            },
                                            notif.acked ? styles.ackedNotif : undefined,
                                        ]}
                                        onClick={() => onNotificationItemClicked(notif)}
                                    >
                                        {/* Avatar */}
                                        <div
                                            onClick={() => onNotificationItemClicked(notif)}
                                            css={{ flexShrink: 0, cursor: 'pointer' }}
                                        >
                                            <Avatar
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    flexShrink: 0,
                                                }}
                                                icon={<UserOutlined />}
                                                src={notif.imageUrl || undefined}
                                            />
                                        </div>

                                        {/* Content */}
                                        <div
                                            className="item-container"
                                            onClick={() => onNotificationItemClicked(notif)}
                                            css={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                        >
                                            <div
                                                css={{
                                                    fontSize: '14px',
                                                    lineHeight: '1.4',
                                                    marginBottom: '4px',
                                                    wordBreak: 'break-word',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                                title={translate(notif)}
                                            >
                                                {translate(notif)}
                                            </div>

                                            <div
                                                className="item-time"
                                                css={{
                                                    fontSize: '12px',
                                                    color: '#999',
                                                    opacity: 0.7,
                                                }}
                                            >
                                                {_formatTime(notif.createdAt)}
                                            </div>
                                        </div>

                                        {/* Delete Button */}
                                        <div className="btn-notif-delete" css={{ flexShrink: 0 }}>
                                            <Button
                                                size="small"
                                                shape="circle"
                                                icon={<CloseOutlined />}
                                                type="text"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleDeleteNotification(notif, index);
                                                }}
                                                css={{
                                                    border: 'none',
                                                    color: '#999',
                                                    '&:hover': {
                                                        color: '#ff4d4f',
                                                        backgroundColor: '#fff2f0',
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </div>
            <div css={styles.notifSetting}>
                <span css={styles.btnSeeAllNotif} onClick={goToNotificationsPage}>
                    {t('see_all_notif')}
                </span>
                <span css={styles.btnDeleteAllNotif} onClick={handleDeleteAllNotif}>
                    {t('delete_all_notif')}
                </span>
            </div>
        </div>
    );
};

export default NotificationContent;
